/**
 * AssuranceCard (P154) -- Home tile for admin OR approver, linking to
 * the "answer the regulator" surface. Mirrors BoardSnapshotCard's
 * visibility (exec-shaped; operators see nothing).
 *
 * B3 -- and it says whether a supervisor is waiting.
 *
 * For five rounds this card read the same whether the bank owed a
 * regulator an answer or not, so the only way to find out was to open
 * /assurance and look. A waiting supervisory question is the most
 * time-sensitive thing this product holds, and home was silent about
 * it. My Work carries the row; home is where an approver starts.
 *
 * The count is not decoration: with nothing open the card keeps its
 * standing description, because "0 questions open" on a permanent tile
 * trains a reader to stop reading the line.
 */
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";

import { MarsadIcon } from "shared/components/MarsadIcon";
import { useMe } from "shared/api/useMe";
import { api } from "shared/api/client";
import { BrassLink } from "shared/components/BrassLink";
import { Card } from "shared/components/Card";

export function AssuranceCard() {
  const { t } = useTranslation();
  const me = useMe();
  const canSee = me.data?.is_admin || me.data?.positions.includes("approver");
  // Only an approver may list them; an admin seeing the card would
  // otherwise fire a request that 403s on every home render.
  const canCount = Boolean(me.data?.positions.includes("approver"));
  const open = useQuery<{ id: string }[]>({
    queryKey: ["assurance", "rfis", "open"],
    queryFn: () => api.get<{ id: string }[]>("/v1/assurance/rfis?open_only=true"),
    enabled: canCount,
    // A supervisor's question arrives while somebody is looking at
    // home, not on a page load.
    refetchInterval: 60_000,
  });
  if (!me.data || !canSee) {
    return null;
  }
  const waiting = open.data?.length ?? 0;
  return (
    <Card testid="assurance-card" className="space-y-2">
      <p className="flex items-center gap-1.5 font-mono text-[10.5px] uppercase tracking-[0.18em] text-ink-muted">
        <MarsadIcon name="verifier" size={13} className="text-brand-brassDark" />
        {t("assurance.card.heading")}
      </p>
      {waiting > 0 ? (
        <p
          data-testid="assurance-card-waiting"
          className="text-[12.5px] text-rag-amber"
        >
          {t("assurance.card.waiting", { count: waiting })}
        </p>
      ) : (
        <p className="text-[12.5px] text-ink-2">{t("assurance.card.body")}</p>
      )}
      <BrassLink to="/assurance" testid="assurance-card-open" className="inline-block">
        {waiting > 0 ? t("assurance.card.answer") : t("assurance.card.open")}
      </BrassLink>
    </Card>
  );
}
